import { useQuery } from "@tanstack/react-query";
import { CircleDollarSign, Cpu, Globe } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatUsdCost } from "@/pages/tina/lib/format-cost";
import { useRunsRepository } from "@/pages/tina/runs/hooks/use-runs-repository";
import type { RunCostBreakdown } from "@/types/tina";

interface CostBreakdownDialogProps {
  runId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CostBreakdownDialog({
  runId,
  open,
  onOpenChange,
}: CostBreakdownDialogProps) {
  const repo = useRunsRepository();

  const { data, isLoading, isError, error } = useQuery<RunCostBreakdown>({
    queryKey: ["tina", "runs", runId, "cost-breakdown"],
    queryFn: () => repo.getCostBreakdown(runId),
    enabled: open && !!runId,
    staleTime: 30_000,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CircleDollarSign className="h-4 w-4 text-indigo-500" />
            Cost breakdown
            {data && (
              <span className="ml-auto font-mono text-base tabular-nums">
                {formatUsdCost(data.total_cost)}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Per-model LLM cost and per-API external service cost for this run.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-32 rounded-md" />
            <Skeleton className="h-32 rounded-md" />
          </div>
        )}

        {isError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            Failed to load cost breakdown: {String(error)}
          </div>
        )}

        {data && !isLoading && (
          <div className="flex flex-col gap-4">
            <LlmSection breakdown={data.llm} />
            <ApiSection breakdown={data.api} />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Estimate. Per-call rate is the published peak rate (top tier).
              Google Maps Platform grants a monthly free allowance per SKU
              (e.g. 5 000 Text Search Pro, 5 000 Place Details Pro, 1 000
              Photos) and volume discounts above 100 k calls — actual billed
              cost will be lower. Cached Places lookups cost $0.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LlmSection({ breakdown }: { breakdown: RunCostBreakdown["llm"] }) {
  return (
    <section className="rounded-md border">
      <header className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            LLM models
          </h3>
        </div>
        <span className="font-mono text-xs tabular-nums">
          {formatUsdCost(breakdown.total_cost)}
        </span>
      </header>
      {breakdown.models.length === 0 ? (
        <p className="p-3 text-xs text-muted-foreground">No LLM calls.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Model</TableHead>
              <TableHead className="text-right">Calls</TableHead>
              <TableHead className="text-right">In</TableHead>
              <TableHead className="text-right">Out</TableHead>
              <TableHead className="text-right">Think</TableHead>
              <TableHead className="text-right">Cached</TableHead>
              <TableHead className="text-right">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {breakdown.models.map((row) => (
              <TableRow key={row.model ?? "(unknown)"}>
                <TableCell className="font-mono text-xs">
                  {row.model ?? "—"}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-xs">
                  {row.calls}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-xs">
                  {row.input_tokens.toLocaleString()}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-xs">
                  {row.output_tokens.toLocaleString()}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-xs">
                  {row.thinking_tokens.toLocaleString()}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-xs">
                  {row.cached_tokens.toLocaleString()}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums text-xs">
                  {formatUsdCost(row.cost)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

function ApiSection({ breakdown }: { breakdown: RunCostBreakdown["api"] }) {
  return (
    <section className="rounded-md border">
      <header className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <div className="flex items-center gap-2">
          <Globe className="h-3.5 w-3.5 text-muted-foreground" />
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            External APIs
          </h3>
        </div>
        <span className="font-mono text-xs tabular-nums">
          {formatUsdCost(breakdown.total_cost)}
        </span>
      </header>
      {breakdown.providers.length === 0 ? (
        <p className="p-3 text-xs text-muted-foreground">
          No external API calls.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>API</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead className="text-right">Calls</TableHead>
              <TableHead className="text-right">Cost</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {breakdown.providers.flatMap((provider) =>
              provider.skus.map((sku, index) => (
                <TableRow key={`${provider.api}:${sku.sku}`}>
                  <TableCell className="font-mono text-xs">
                    {index === 0 ? formatApi(provider.api) : ""}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{sku.sku}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-xs">
                    {sku.calls}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-xs">
                    {formatUsdCost(sku.cost)}
                  </TableCell>
                </TableRow>
              )),
            )}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

const API_LABELS: Record<string, string> = {
  google_places: "Google Places",
};

function formatApi(api: string): string {
  return API_LABELS[api] ?? api;
}
